import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import {
  PhysicsWorld,
  COLLISION_GROUP_PEDESTRIAN,
  COLLISION_GROUP_CHASSIS,
  COLLISION_GROUP_AI_VEHICLE,
} from '../core/PhysicsWorld';

export type PedestrianState = 'APPROACHING_CURB' | 'WAITING_AT_CURB' | 'CROSSING' | 'SIDEWALK_PAUSE';

export class AIPedestrian {
  public mesh: THREE.Group;
  public position = new THREE.Vector3();
  public startPos: THREE.Vector3;
  public endPos: THREE.Vector3;
  public progress: number = 0;
  public speed: number = 1.35; // Walking speed ~4.8 km/h
  public state: PedestrianState = 'CROSSING';
  public stateTimer: number = 0;
  public isCrossing: boolean = true;
  public body: CANNON.Body | null = null;

  private head!: THREE.Mesh;
  private legL!: THREE.Mesh;
  private legR!: THREE.Mesh;
  private armL!: THREE.Mesh;
  private armR!: THREE.Mesh;
  private walkTime: number = 0;

  constructor(start: THREE.Vector3, end: THREE.Vector3, physicsWorld?: PhysicsWorld) {
    this.startPos = start.clone();
    this.endPos = end.clone();
    this.position.copy(start);

    this.mesh = new THREE.Group();
    this.buildPedestrianMesh();
    this.mesh.position.copy(this.position);

    if (physicsWorld) {
      this.initPhysicsBody(physicsWorld);
    }
  }

  private initPhysicsBody(physicsWorld: PhysicsWorld) {
    const pedShape = new CANNON.Cylinder(0.28, 0.28, 1.75, 8);
    this.body = new CANNON.Body({
      type: CANNON.Body.KINEMATIC,
      material: physicsWorld.pedestrianMaterial,
      collisionFilterGroup: COLLISION_GROUP_PEDESTRIAN,
      collisionFilterMask: COLLISION_GROUP_CHASSIS | COLLISION_GROUP_AI_VEHICLE,
    });
    this.body.addShape(pedShape, new CANNON.Vec3(0, 0.88, 0));
    this.body.position.set(this.position.x, this.position.y, this.position.z);
    physicsWorld.world.addBody(this.body);
  }

  private buildPedestrianMesh() {
    // High-visibility German urban pedestrian clothing (Red outdoor jacket)
    const jacketMat = new THREE.MeshStandardMaterial({
      color: 0xef4444,
      roughness: 0.5,
    });
    const skinMat = new THREE.MeshStandardMaterial({ color: 0xfcd34d, roughness: 0.6 });
    const hairMat = new THREE.MeshStandardMaterial({ color: 0x451a03, roughness: 0.8 });
    const trousersMat = new THREE.MeshStandardMaterial({ color: 0x1e3a8a, roughness: 0.7 });
    const shoeMat = new THREE.MeshStandardMaterial({ color: 0x0f172a, roughness: 0.4 });

    // 1. Torso & Jacket
    const torso = new THREE.Mesh(new THREE.BoxGeometry(0.40, 0.62, 0.24), jacketMat);
    torso.position.y = 1.12;
    torso.castShadow = true;

    // 2. Head & Hair
    this.head = new THREE.Mesh(new THREE.SphereGeometry(0.13, 14, 14), skinMat);
    this.head.position.y = 1.56;
    this.head.castShadow = true;

    const hair = new THREE.Mesh(new THREE.SphereGeometry(0.14, 12, 12), hairMat);
    hair.position.set(0, 1.59, -0.02);
    hair.scale.set(1.0, 0.7, 1.05);

    // 3. Arms
    const armGeom = new THREE.BoxGeometry(0.11, 0.52, 0.11);
    this.armL = new THREE.Mesh(armGeom, jacketMat);
    this.armL.position.set(-0.27, 1.12, 0);
    this.armR = new THREE.Mesh(armGeom, jacketMat);
    this.armR.position.set(0.27, 1.12, 0);

    // 4. Legs & Shoes
    const legGeom = new THREE.BoxGeometry(0.13, 0.68, 0.14);
    this.legL = new THREE.Mesh(legGeom, trousersMat);
    this.legL.position.set(-0.11, 0.44, 0);
    this.legL.castShadow = true;

    this.legR = new THREE.Mesh(legGeom, trousersMat);
    this.legR.position.set(0.11, 0.44, 0);
    this.legR.castShadow = true;

    const shoeGeom = new THREE.BoxGeometry(0.14, 0.10, 0.22);
    const shoeL = new THREE.Mesh(shoeGeom, shoeMat);
    shoeL.position.set(-0.11, 0.05, 0.04);
    const shoeR = new THREE.Mesh(shoeGeom, shoeMat);
    shoeR.position.set(0.11, 0.05, 0.04);

    this.mesh.add(torso, this.head, hair, this.armL, this.armR, this.legL, this.legR, shoeL, shoeR);
  }

  public update(deltaTime: number, playerPos?: THREE.Vector3, vehicles?: { position: THREE.Vector3; halfLength?: number }[]) {
    this.isCrossing = this.state === 'CROSSING' || this.state === 'WAITING_AT_CURB';

    // Handle SIDEWALK_PAUSE and WAITING_AT_CURB states between crossings
    if (this.state === 'SIDEWALK_PAUSE') {
      this.stateTimer -= deltaTime;
      if (this.legL && this.legR) {
        this.legL.rotation.x *= 0.8;
        this.legR.rotation.x *= 0.8;
        this.armL.rotation.x *= 0.8;
        this.armR.rotation.x *= 0.8;
      }
      if (this.head) this.head.rotation.y = 0;
      if (this.stateTimer <= 0) {
        this.state = 'WAITING_AT_CURB';
        this.stateTimer = 1.0;
        this.isCrossing = true;
      }
      return;
    }

    if (this.state === 'WAITING_AT_CURB') {
      this.stateTimer -= deltaTime;
      if (this.head) {
        this.head.rotation.y = Math.sin(this.stateTimer * Math.PI * 2) * 0.45;
      }
      // Orientation towards upcoming crossing
      const dir = this.endPos.clone().sub(this.startPos).normalize();
      this.mesh.rotation.y = Math.atan2(dir.x, dir.z);
      if (this.stateTimer <= 0) {
        this.state = 'CROSSING';
        this.isCrossing = true;
        if (this.head) this.head.rotation.y = 0;
      }
      return;
    }

    let obstacleNear = !!(playerPos && this.position.distanceTo(playerPos) < 2.8);
    if (!obstacleNear && vehicles && vehicles.length > 0) {
      for (const v of vehicles) {
        const thresh = (v.halfLength ?? 2.2) + 0.8;
        if (this.position.distanceTo(v.position) < thresh) {
          obstacleNear = true;
          break;
        }
      }
    }

    if (obstacleNear) {
      // Yield to stationary or passing car — pause stride and avoid clipping
      if (this.legL && this.legR) {
        this.legL.rotation.x *= 0.8;
        this.legR.rotation.x *= 0.8;
        this.armL.rotation.x *= 0.8;
        this.armR.rotation.x *= 0.8;
      }
      return;
    }

    const totalDist = Math.max(0.1, this.startPos.distanceTo(this.endPos));
    this.progress += (this.speed * deltaTime) / totalDist;
    this.walkTime += deltaTime * 5.5; // Walk cycle oscillation speed

    if (this.progress >= 1.0) {
      this.progress = 0;
      const temp = this.startPos.clone();
      this.startPos.copy(this.endPos);
      this.endPos.copy(temp);
      this.position.copy(this.startPos);
      this.mesh.position.copy(this.position);
      this.state = 'SIDEWALK_PAUSE';
      this.stateTimer = 2.5;
      this.isCrossing = false;
      return;
    }

    this.position.lerpVectors(this.startPos, this.endPos, this.progress);
    this.mesh.position.copy(this.position);

    // Natural walking stride animation
    if (this.legL && this.legR) {
      this.legL.rotation.x = Math.sin(this.walkTime) * 0.42;
      this.legR.rotation.x = -Math.sin(this.walkTime) * 0.42;
      this.armL.rotation.x = -Math.sin(this.walkTime) * 0.35;
      this.armR.rotation.x = Math.sin(this.walkTime) * 0.35;
    }

    // Orientation
    const dir = this.endPos.clone().sub(this.startPos).normalize();
    this.mesh.rotation.y = Math.atan2(dir.x, dir.z);

    if (this.body) {
      this.body.position.set(this.position.x, this.position.y, this.position.z);
      this.body.quaternion.setFromEuler(0, this.mesh.rotation.y, 0);
    }
  }
}
